import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { pickBy } from 'lodash';
import { MdShowChart } from 'react-icons/md';
import { BiBarChartAlt } from 'react-icons/bi';

import Button from 'partial/components/Button';
import { BarChart, HorizontalBarChart } from 'modules/common/chart/BarChart';
import LineChart from 'modules/common/chart/LineChart';
import FormSelectDate from 'modules/transactions/components/FormSelectDate';
import HistogramRange from 'modules/transactions/components/HistogramRange';

export const useHistogramCard = (array, defaultIndex = 0) => {
  const [props, setProps] = useState(array[defaultIndex]);
  const output = useMemo(
    () => ({
      key: props.id,
      ...props,
      actionOptions: array.map((x) => ({
        id: x.id,
        label: x.label,
        onClick: () => {
          setProps(x);
        },
      })),
    }),
    [array, props, setProps],
  );
  return output;
};

const MAP_TYPE = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

function HistogramCard({
  hook,
  dateRange,
  switchMode,
  hasSelectDate,
  yearOnly,
  defaultMode,
  customHeight,
  defaultChart,
  withActions,
  containerClassName,
  customChartPadding,
  setFilter: setOtherFilter,
  filter: otherFilter,
}) {
  const [chartType, setChartType] = useState(defaultChart);

  const [filter, setFilter] = useState({
    type: MAP_TYPE[defaultMode],
    date_from: dayjs().startOf(defaultMode).format('YYYY-MM-DD 00:00:00'),
    date_to: dayjs().endOf(defaultMode).format('YYYY-MM-DD 23:59:59'),
  });

  const [isLoading, chart] = hook(pickBy({ ...otherFilter, ...filter }));

  useEffect(() => {
    setOtherFilter((prev) => ({ ...prev, ...filter }));
  }, [filter, setOtherFilter]);

  const toggleChart = useCallback(
    () => (
      <div className="flex items-center justify-between">
        <div>
          {dateRange && (
            <div className="space-x-1 flex mt-2 sm:mt-0">
              <HistogramRange
                onChange={setFilter}
                defaultValue={dayjs().format('YYYY-MM-DD')}
                defaultMode={defaultMode}
                switchMode={switchMode}
              />
            </div>
          )}

          {hasSelectDate && (
            <div className="ml-auto">
              <FormSelectDate
                onChange={setFilter}
                defaultMode={defaultMode}
                yearOnly={yearOnly}
              />
            </div>
          )}
        </div>
        <div className="space-x-1 flex justify-end">
          <Button
            size="xs"
            primary={chartType === 'line'}
            icon={MdShowChart}
            onClick={() => setChartType('line')}
          />
          <Button
            size="xs"
            primary={chartType === 'bar'}
            icon={BiBarChartAlt}
            onClick={() => setChartType('bar')}
          />
        </div>
      </div>
    ),
    [
      setFilter,
      chartType,
      setChartType,
      defaultMode,
      switchMode,
      dateRange,
      yearOnly,
      hasSelectDate,
    ],
  );

  return (
    <div className={containerClassName} style={{ height: customHeight ?? 300 }}>
      {withActions && toggleChart()}
      {chartType === 'line' && <LineChart data={chart} isLoading={isLoading} />}
      {chartType === 'bar' && (
        <BarChart data={chart} horizontal={false} isLoading={isLoading} />
      )}
      {chartType === 'horizontal-bar' && (
        <HorizontalBarChart
          data={chart}
          horizontal
          isLoading={isLoading}
          {...customChartPadding}
        />
      )}
    </div>
  );
}

HistogramCard.defaultProps = {
  filter: {},
  dateRange: true,
  switchMode: false,
  hasSelectDate: false,
  yearOnly: false,
  defaultMode: 'day',
  defaultChart: 'line',
  customHeight: null,
  withActions: false,
  containerClassName: '',
  customChartPadding: {},
  setFilter: () => {},
};

HistogramCard.propTypes = {
  dateRange: PropTypes.bool,
  yearOnly: PropTypes.bool,
  switchMode: PropTypes.bool,
  hasSelectDate: PropTypes.bool,
  withActions: PropTypes.bool,
  defaultMode: PropTypes.string,
  defaultChart: PropTypes.string,
  hook: PropTypes.func.isRequired,
  containerClassName: PropTypes.string,
  setFilter: PropTypes.func,
  filter: PropTypes.instanceOf(Object),
  customHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customChartPadding: PropTypes.instanceOf(Object),
};

export default HistogramCard;
